import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Checkbox,
  IconButton,
  Chip,
  Skeleton,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Star,
  StarOff,
  Paperclip,
  Mail,
  MailOpen,
  Archive,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { formatDistanceToNow, format, isToday, isYesterday, isThisYear } from 'date-fns';

/**
 * MailList - Email inbox list component
 * Displays threads/messages with selection, actions, and infinite scroll
 */
const MailList = ({
  threads = [],
  selectedId = null,
  onSelect,
  onStar,
  onMarkRead,
  onArchive,
  onDelete,
  loading = false,
  loadingMore = false,
  onLoadMore,
  hasMore = false,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  emptyMessage = 'No emails found',
  compact = false,
}) => {
  // Format date for display
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    
    if (isToday(date)) {
      return format(date, 'h:mm a');
    }
    if (isYesterday(date)) {
      return 'Yesterday';
    }
    if (isThisYear(date)) {
      return format(date, 'MMM d');
    }
    return format(date, 'MMM d, yyyy');
  }, []);

  // Get initials for avatar
  const getInitials = useCallback((name, email) => {
    if (name) {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return '?';
  }, []);

  // Get display name
  const getDisplayName = useCallback((sender) => {
    if (!sender) return 'Unknown';
    return sender.name || sender.email?.split('@')[0] || 'Unknown';
  }, []);

  // Handle selection toggle
  const handleSelectionToggle = useCallback((threadId) => {
    if (!onSelectionChange) return;
    
    const newSelection = selectedIds.includes(threadId)
      ? selectedIds.filter(id => id !== threadId)
      : [...selectedIds, threadId];
    
    onSelectionChange(newSelection);
  }, [selectedIds, onSelectionChange]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback((e) => {
    if (!onLoadMore || !hasMore || loadingMore) return;
    
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      onLoadMore();
    }
  }, [onLoadMore, hasMore, loadingMore]);

  // Render loading skeleton
  const renderSkeleton = useMemo(() => (
    Array.from({ length: 5 }).map((_, index) => (
      <ListItem key={`skeleton-${index}`} divider>
        <ListItemAvatar>
          <Skeleton variant="circular" width={40} height={40} />
        </ListItemAvatar>
        <ListItemText
          primary={<Skeleton width="60%" />}
          secondary={<Skeleton width="80%" />}
        />
      </ListItem>
    ))
  ), []);

  // Render empty state
  if (!loading && threads.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 2,
          color: 'text.secondary',
        }}
      >
        <Mail size={48} strokeWidth={1} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
      }}
      onScroll={handleScroll}
    >
      <List disablePadding>
        {loading && threads.length === 0 ? (
          renderSkeleton
        ) : (
          threads.map((thread) => {
            const message = thread.latest || thread;
            const isSelected = selectedId === thread.id;
            const isChecked = selectedIds.includes(thread.id);
            const hasUnread = thread.hasUnread || message.unread;
            const sender = message.sender || {};

            return (
              <ListItem
                key={thread.id}
                disablePadding
                divider
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {message.hasAttachments && (
                      <Tooltip title="Has attachments">
                        <Paperclip size={14} style={{ color: '#666' }} />
                      </Tooltip>
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ minWidth: 60, textAlign: 'right' }}
                    >
                      {formatDate(message.receivedOn)}
                    </Typography>
                  </Box>
                }
                sx={{
                  bgcolor: isSelected ? 'action.selected' : 'transparent',
                  '&:hover': {
                    bgcolor: isSelected ? 'action.selected' : 'action.hover',
                  },
                }}
              >
                <ListItemButton
                  onClick={() => onSelect?.(thread.id)}
                  sx={{ pr: 12 }}
                >
                  {selectable && (
                    <Checkbox
                      checked={isChecked}
                      onChange={() => handleSelectionToggle(thread.id)}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                  )}
                  
                  <ListItemAvatar>
                    <Badge
                      color="primary"
                      variant="dot"
                      invisible={!hasUnread}
                      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: hasUnread ? 'primary.main' : 'grey.400',
                          width: compact ? 32 : 40,
                          height: compact ? 32 : 40,
                          fontSize: compact ? 12 : 14,
                        }}
                      >
                        {getInitials(sender.name, sender.email)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: hasUnread ? 600 : 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 180,
                          }}
                        >
                          {getDisplayName(sender)}
                        </Typography>
                        {thread.messageCount > 1 && (
                          <Chip
                            label={thread.messageCount}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: 11,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box component="span">
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            fontWeight: hasUnread ? 600 : 400,
                            color: 'text.primary',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {message.subject || '(No subject)'}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {message.bodyPreview || ''}
                        </Typography>
                      </Box>
                    }
                    primaryTypographyProps={{
                      component: 'div',
                    }}
                    secondaryTypographyProps={{
                      component: 'div',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })
        )}
        
        {loadingMore && (
          <ListItem>
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 2 }}>
              <RefreshCw size={20} className="animate-spin" />
            </Box>
          </ListItem>
        )}
      </List>
    </Box>
  );
};

MailList.propTypes = {
  threads: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      latest: PropTypes.object,
      hasUnread: PropTypes.bool,
      messageCount: PropTypes.number,
    })
  ),
  selectedId: PropTypes.string,
  onSelect: PropTypes.func,
  onStar: PropTypes.func,
  onMarkRead: PropTypes.func,
  onArchive: PropTypes.func,
  onDelete: PropTypes.func,
  loading: PropTypes.bool,
  loadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  selectable: PropTypes.bool,
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  onSelectionChange: PropTypes.func,
  emptyMessage: PropTypes.string,
  compact: PropTypes.bool,
};

export default MailList;
